import { transformParamsToCrudApi } from '@app/core/crud/listing-adapter';
import { convertGetManyResponse } from '@app/core/crud/response-converter';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { ListView } from '@app/core/list-view/ListView';
import { ListViewQueryParams } from '@app/core/list-view/types';
import {
  auditReportsListReportRunsCatalog,
  AuditReportsListReportRunsCatalogQueryParams,
  AuditReportsReportRun,
} from '@mi-front/api-v2';
import { cleanQuery, Paginated } from '@mi-front/core';
import { SAVED_REPORTS_BROADCAST_EVIDENCE_FILTERS_CONFIG } from '../filters/saved-reports-list-filters-config';
import { SavedReportListItem } from '../types';
import { SavedReportsBroadcastEvidenceTable } from './SavedReportsBroadcastEvidenceTable';

const BROADCAST_EVIDENCE_TYPE = 'AUDIT_BROADCAST_EVIDENCE';

/**
 * Station id from config or the first metadata.stationIds entry.
 */
function getReportRunStationId(run: AuditReportsReportRun): string | undefined {
  const fromConfig = run.config?.stationId;
  if (typeof fromConfig === 'string' && fromConfig) {
    return fromConfig;
  }
  const fromMetadata = run.metadata?.stationIds;
  if (Array.isArray(fromMetadata) && typeof fromMetadata[0] === 'string') {
    return fromMetadata[0];
  }
  return undefined;
}

/**
 * Maps a report-run into the shared list row shape.
 */
function mapReportRun(run: AuditReportsReportRun): SavedReportListItem {
  return {
    id: run.id,
    name: run.name,
    type: run.type,
    createdAt: run.createdAt,
    createdByUserId: run.createdByUserId,
    stationId: getReportRunStationId(run),
    job: run.job,
  };
}

/**
 * Broadcast Evidence saved reports via report-runs CRUD (date + name filters).
 */
export function SavedReportsBroadcastEvidenceList() {
  const timezone = useTimezone();

  const queryFn = async (params: ListViewQueryParams): Promise<Paginated<SavedReportListItem>> => {
    const processedParams = transformParamsToCrudApi(params, {
      filterDefinitions: SAVED_REPORTS_BROADCAST_EVIDENCE_FILTERS_CONFIG,
      context: { timezone },
    });

    const response = await auditReportsListReportRunsCatalog(
      cleanQuery({
        ...processedParams,
        filter: [`type||eq||${BROADCAST_EVIDENCE_TYPE}`, ...(processedParams.filter ?? [])],
      }) as AuditReportsListReportRunsCatalogQueryParams,
    );

    const page = await convertGetManyResponse(response);

    return {
      ...page,
      results: page.results.map(mapReportRun),
    };
  };

  return (
    <ListView
      listId="saved-reports-AUDIT_BROADCAST_EVIDENCE"
      filters={SAVED_REPORTS_BROADCAST_EVIDENCE_FILTERS_CONFIG}
      queryFn={queryFn}
      ListComponent={SavedReportsBroadcastEvidenceTable}
    />
  );
}
